import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import { Input, Form, Select, Typography } from 'antd';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const { Option } = Select;
const { Text } = Typography;

interface LocationPickerProps {
  value?: {
    address: string;
    city: string;
    latitude: number;
    longitude: number;
  };
  onChange?: (value: {
    address: string;
    city: string;
    latitude: number;
    longitude: number;
  }) => void;
}

const defaultCenter = {
  lat: 44.7322,
  lng: 18.0878
};

const cities = [
  'Doboj',
  'Banja Luka',
  'Sarajevo',
  'Tuzla',
  'Mostar',
  'Prijedor',
  'Bijeljina',
  'Istočno Sarajevo',
  'Brčko'
].sort();

// Fix for default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface MapClickHandlerProps {
  onMapClick: (lat: number, lng: number) => void;
}

const MapClickHandler: React.FC<MapClickHandlerProps> = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const LocationPicker: React.FC<LocationPickerProps> = ({ value, onChange }) => {
  const [internalValue, setInternalValue] = useState({
    address: value?.address || '',
    city: value?.city || '',
    latitude: value?.latitude || defaultCenter.lat,
    longitude: value?.longitude || defaultCenter.lng
  });

  useEffect(() => {
    if (value) {
      setInternalValue(value);
    }
  }, [value]);

  const handleValueChange = useCallback((newValue: typeof internalValue) => {
    setInternalValue(newValue);
    onChange?.(newValue);
  }, [onChange]);

  const handleMapClick = useCallback((lat: number, lng: number) => {
    handleValueChange({
      ...internalValue,
      latitude: lat,
      longitude: lng
    });
  }, [internalValue, handleValueChange]);

  const handleAddressChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleValueChange({
      ...internalValue,
      address: e.target.value
    });
  }, [internalValue, handleValueChange]);

  const handleCityChange = useCallback((city: string) => {
    handleValueChange({
      ...internalValue,
      city
    });
  }, [internalValue, handleValueChange]);

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        <Select
          className="w-full"
          placeholder="Odaberite grad"
          value={internalValue.city || undefined}
          onChange={handleCityChange}
        >
          {cities.map(city => (
            <Option key={city} value={city}>{city}</Option>
          ))}
        </Select>

        <Input
          placeholder="Unesite ulicu i broj"
          value={internalValue.address}
          onChange={handleAddressChange}
        />
      </div>

      <div style={{ height: '400px', width: '100%', position: 'relative' }}>
        <MapContainer
          center={[internalValue.latitude, internalValue.longitude]}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker 
            position={[internalValue.latitude, internalValue.longitude]}
            draggable={true}
            eventHandlers={{
              dragend: (e) => {
                const marker = e.target;
                const position = marker.getLatLng();
                handleMapClick(position.lat, position.lng);
              },
            }}
          />
          <MapClickHandler onMapClick={handleMapClick} />
        </MapContainer>
      </div>

      <Text type="secondary">
        Kliknite na mapu da označite tačnu lokaciju ili prevucite marker
      </Text>
    </div>
  );
};

export default LocationPicker;